/**
 * Devices Management - Platform-aware view
 */
import React, { useEffect, useState } from "react";
import {
  listDevices,
  getDeviceStats,
  getDeviceSharing,
  blockAllDevicesOnKey,
  updateDeviceStatus,
  deleteDevice,
  bulkDeleteDevices,
} from "../lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  Monitor,
  Smartphone,
  Tablet,
  MonitorSmartphone,
  Ban,
  CheckCircle2,
  Trash2,
  Apple,
  AlertTriangle,
  Globe,
  ChevronDown,
  ChevronUp,
  Database,
  Wand2,
} from "lucide-react";
import { ADMIN } from "../lib/testIds";

const platformIcons = {
  windows: Monitor,
  macos: Apple,
  linux: Monitor,
  android: Smartphone,
  ios: Smartphone,
  tv: Tablet,
  web: Monitor,
};

const platformLabels = {
  windows: "Windows",
  macos: "macOS",
  linux: "Linux",
  android: "Android",
  ios: "iOS",
  web: "Web",
  tv: "TV",
};

const statusColors = {
  active: "bg-emerald-100 text-emerald-700",
  inactive: "bg-slate-100 text-slate-700",
  blocked: "bg-red-100 text-red-700",
};

export default function Devices() {
  const { toast } = useToast();
  const [devices, setDevices] = useState([]);
  const [stats, setStats] = useState(null);
  const [sharing, setSharing] = useState(null); // v1.3.77 credential-sharing report
  const [sharingExpanded, setSharingExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [platformFilter, setPlatformFilter] = useState("all");
  // v1.3.77 — when a sharing row is clicked, scope the table to its key
  const [sharingKeyFilter, setSharingKeyFilter] = useState(null);
  // v1.3.82 — checkbox selection + bulk delete
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const refresh = async () => {
    setLoading(true);
    try {
      const filters = platformFilter !== "all" ? { platform: platformFilter } : {};
      const [d, s, sh] = await Promise.all([
        listDevices(filters),
        getDeviceStats(),
        getDeviceSharing().catch(() => ({ total_shared_keys: 0, rows: [] })),
      ]);
      setDevices(d);
      setStats(s);
      setSharing(sh);
    } catch (e) {
      toast({ title: "Failed to load", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [platformFilter]);

  const handleToggleBlock = async (device) => {
    const newStatus = device.status === "blocked" ? "active" : "blocked";
    try {
      await updateDeviceStatus(device.id, newStatus);
      toast({ title: `Device ${newStatus}` });
      refresh();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  // v1.4.69 — Self-heal action. Hits the new
  // `POST /admin/devices/{id}/self-heal` endpoint which resets a
  // stuck device row (blocked, inactive, stale last_seen, bloated
  // seen_ips) back to a clean `active` state.
  const handleSelfHeal = async (device) => {
    try {
      const API_URL = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      const res = await fetch(`${API_URL}/admin/devices/${device.id}/self-heal`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("admin_token") || ""}`,
        },
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      toast({ title: "Device self-healed", description: "Status reset to active." });
      refresh();
    } catch (e) {
      toast({ title: "Self-heal failed", description: e.message, variant: "destructive" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this device? The user can re-activate with the same key.")) return;
    try {
      await deleteDevice(id);
      toast({ title: "Device removed" });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      refresh();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  // v1.3.82 — Bulk-delete the checkbox selection. One confirm prompt,
  // one round-trip, one refresh. Backend also purges per-device
  // pre-warm bundles.
  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (
      !window.confirm(
        `Remove ${ids.length} selected device(s)?\n\n` +
          `Their per-device pre-warm catalog bundles will also be deleted from disk. ` +
          `Users can re-activate on the same key any time.`,
      )
    )
      return;
    try {
      const res = await bulkDeleteDevices(ids);
      toast({
        title: "Devices removed",
        description:
          res?.message || `Deleted ${res?.data?.deleted ?? ids.length} device(s)`,
      });
      setSelectedIds(new Set());
      refresh();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  /**
   * v1.3.78 — Bulk-block every device on a shared activation key.
   * Triggered from the credential-sharing alert. Confirms first
   * because this affects an unknown number of devices and is the
   * most destructive action on this screen.
   */
  const handleBlockAllOnKey = async (activationKey, deviceCount) => {
    const msg =
      `Block all ${deviceCount} device(s) currently using activation key ` +
      `${activationKey}?\n\n` +
      `Every device on this key will be immediately denied access ` +
      `until you un-block it individually. The key itself stays active — ` +
      `release or re-issue it via the Keys page if needed.`;
    if (!window.confirm(msg)) return;
    try {
      const res = await blockAllDevicesOnKey(activationKey);
      toast({
        title: "Devices blocked",
        description:
          res?.message ||
          `Blocked ${res?.data?.modified_count ?? deviceCount} device(s)`,
      });
      refresh();
    } catch (e) {
      toast({
        title: "Failed to block",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      <p className="text-slate-600">
        All activated devices across platforms (Windows, Android, iOS, etc.)
      </p>

      {/* Platform stat cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="text-xs text-slate-500 uppercase">Total Devices</div>
            <div className="text-2xl font-bold text-slate-900 mt-1">{stats.total}</div>
            <div className="text-xs text-emerald-600 mt-1">{stats.active} active</div>
          </div>
          {Object.entries(stats.by_platform || {}).map(([platform, count]) => {
            const Icon = platformIcons[platform] || MonitorSmartphone;
            return (
              <div key={platform} className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-xs text-slate-500 uppercase">
                  <Icon className="w-3.5 h-3.5" />
                  {platformLabels[platform] || platform}
                </div>
                <div className="text-2xl font-bold text-slate-900 mt-1">{count}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* v1.3.77 — Credential-sharing alert. Lists every activation
          key bound to >1 distinct device_id. Click a row to scope
          the device table below to just that key's devices. */}
      {sharing && sharing.total_shared_keys > 0 && (
        <Card
          className="border-amber-300 bg-amber-50"
          data-testid="device-sharing-alert"
        >
          <CardContent className="p-4">
            <button
              type="button"
              onClick={() => setSharingExpanded((v) => !v)}
              className="w-full flex items-center gap-3 text-left"
              data-testid="device-sharing-toggle"
            >
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <div className="flex-1">
                <div className="font-semibold text-amber-900">
                  {sharing.total_shared_keys} activation key
                  {sharing.total_shared_keys === 1 ? "" : "s"} may be shared
                </div>
                <div className="text-xs text-amber-700 mt-0.5">
                  These keys are bound to more than one distinct MAC address.
                  Click to expand and review — or tap a row to filter the table
                  to just those devices.
                </div>
              </div>
              {sharingExpanded ? (
                <ChevronUp className="w-4 h-4 text-amber-700" />
              ) : (
                <ChevronDown className="w-4 h-4 text-amber-700" />
              )}
            </button>
            {sharingExpanded && (
              <div className="mt-3 space-y-1.5">
                {sharing.rows.map((r) => (
                  <div
                    key={r.activation_key}
                    className={
                      "flex items-center gap-3 text-xs " +
                      "border border-amber-200 rounded-md px-3 py-2 " +
                      "transition-colors " +
                      (sharingKeyFilter === r.activation_key
                        ? "bg-amber-100 border-amber-400"
                        : "bg-white")
                    }
                    data-testid={`sharing-row-${r.activation_key}`}
                  >
                    {/* Clicking the key body filters the table below */}
                    <button
                      type="button"
                      onClick={() =>
                        setSharingKeyFilter(
                          sharingKeyFilter === r.activation_key
                            ? null
                            : r.activation_key,
                        )
                      }
                      className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-80"
                    >
                      <span className="font-mono text-slate-800 truncate flex-1">
                        {r.activation_key}
                      </span>
                      <Badge variant="outline" className="border-amber-300 text-amber-800">
                        {r.device_count} devices
                      </Badge>
                      <span className="text-slate-500 flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        {r.unique_ip_count} IPs
                      </span>
                      <span className="text-slate-400 hidden md:inline">
                        last seen{" "}
                        {r.last_seen ? new Date(r.last_seen).toLocaleString() : "—"}
                      </span>
                    </button>
                    {/* v1.3.78 — one-click bulk-block. Separate button
                        so clicking the key body still just filters. */}
                    <Button
                      data-testid={`sharing-block-all-${r.activation_key}`}
                      size="sm"
                      variant="outline"
                      className="text-red-700 border-red-300 hover:bg-red-50 shrink-0"
                      onClick={() =>
                        handleBlockAllOnKey(r.activation_key, r.device_count)
                      }
                    >
                      <Ban className="w-3.5 h-3.5 mr-1" />
                      Block all
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {sharingKeyFilter && (
              <div className="mt-2 flex items-center gap-2 text-xs text-amber-800">
                <span>
                  Table below is filtered to key{" "}
                  <span className="font-mono">{sharingKeyFilter}</span>
                </span>
                <button
                  type="button"
                  className="underline"
                  onClick={() => setSharingKeyFilter(null)}
                  data-testid="sharing-clear-filter"
                >
                  Clear filter
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-slate-600">Platform:</span>
        <Select value={platformFilter} onValueChange={setPlatformFilter}>
          <SelectTrigger
            data-testid={ADMIN.devicePlatformFilter}
            className="w-44"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Platforms</SelectItem>
            <SelectItem value="windows">Windows</SelectItem>
            <SelectItem value="android">Android</SelectItem>
            <SelectItem value="ios">iOS</SelectItem>
            <SelectItem value="macos">macOS</SelectItem>
            <SelectItem value="web">Web</SelectItem>
            <SelectItem value="linux">Linux</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : (() => {
            // v1.3.77 — apply optional sharing-key filter on top of
            // the platform filter that the backend already applied.
            const filtered = sharingKeyFilter
              ? devices.filter((d) => d.activation_key === sharingKeyFilter)
              : devices;
            if (filtered.length === 0) {
              return (
                <div className="p-12 text-center text-slate-500">
                  <MonitorSmartphone className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>No devices found.</p>
                </div>
              );
            }
            // v1.3.82 — selection helpers scoped to the currently
            // filtered set so the "select all" checkbox reflects only
            // what the admin sees.
            const filteredIds = filtered.map((d) => d.id);
            const selectedInView = filteredIds.filter((id) => selectedIds.has(id));
            const allChecked = selectedInView.length === filtered.length;
            const someChecked = selectedInView.length > 0 && !allChecked;
            const toggleOne = (id) => {
              setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id); else next.add(id);
                return next;
              });
            };
            const toggleAll = () => {
              setSelectedIds((prev) => {
                const next = new Set(prev);
                if (allChecked) {
                  filteredIds.forEach((id) => next.delete(id));
                } else {
                  filteredIds.forEach((id) => next.add(id));
                }
                return next;
              });
            };
            return (
            <>
            {/* v1.3.82 — Bulk selection toolbar. Shown whenever anything
                is selected so the admin can quickly delete a batch. */}
            {selectedIds.size > 0 && (
              <div
                data-testid="device-bulk-toolbar"
                className="flex items-center justify-between gap-3 px-4 py-2.5 bg-slate-50 border-b border-slate-200"
              >
                <div className="text-sm text-slate-700">
                  <span className="font-semibold" data-testid="device-selected-count">
                    {selectedIds.size}
                  </span>{" "}
                  device{selectedIds.size === 1 ? "" : "s"} selected
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    data-testid="btn-clear-selection"
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Clear
                  </Button>
                  <Button
                    data-testid="btn-bulk-delete-devices"
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                    Delete selected
                  </Button>
                </div>
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      data-testid="chk-select-all-devices"
                      checked={allChecked ? true : (someChecked ? "indeterminate" : false)}
                      onCheckedChange={toggleAll}
                      aria-label="Select all devices"
                    />
                  </TableHead>
                  <TableHead>Platform</TableHead>
                  <TableHead>Device ID (MAC)</TableHead>
                  <TableHead>Model</TableHead>
                  <TableHead>OS Version</TableHead>
                  <TableHead>App Ver</TableHead>
                  <TableHead>Activation Key</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Last IP</TableHead>
                  <TableHead>Pre-warm</TableHead>
                  <TableHead>Online</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Seen</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((d) => {
                  const Icon = platformIcons[d.platform] || MonitorSmartphone;
                  // v1.3.77 — flag any device whose seen_ips contains
                  // >= 3 distinct IPs (high churn). We highlight it
                  // with an amber dot so it stands out in the table.
                  const ipChurn = Array.isArray(d.seen_ips) && d.seen_ips.length >= 3;
                  const bundleCount = Number(d.prewarm_bundles || 0);
                  const isSelected = selectedIds.has(d.id);
                  return (
                    <TableRow key={d.id} data-testid={ADMIN.deviceRow(d.id)} className={isSelected ? "bg-blue-50/40" : ""}>
                      <TableCell className="w-10">
                        <Checkbox
                          data-testid={`chk-device-${d.id}`}
                          checked={isSelected}
                          onCheckedChange={() => toggleOne(d.id)}
                          aria-label="Select device"
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-slate-500" />
                          <span className="text-sm font-medium">
                            {platformLabels[d.platform] || d.platform}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        <div className="flex flex-col gap-1">
                          <span>{d.device_id}</span>
                          {Array.isArray(d.speed_tests) && d.speed_tests.length > 0 && (
                            <div
                              className="flex flex-col gap-0.5 font-sans text-[10px] text-slate-500"
                              data-testid={`device-speedtests-${d.id}`}
                            >
                              {d.speed_tests.slice(-2).reverse().map((s, i) => {
                                const dl = s.download_mbps != null
                                  ? `${Number(s.download_mbps).toFixed(1)}↓`
                                  : "—";
                                const ul = s.upload_mbps != null
                                  ? `${Number(s.upload_mbps).toFixed(1)}↑`
                                  : "—";
                                const lat = s.latency_ms != null ? `${s.latency_ms}ms` : "—";
                                const when = s.ts
                                  ? new Date(s.ts).toLocaleString(undefined, {
                                      month: "short",
                                      day: "numeric",
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : "";
                                const tag = s.target === "provider" ? "PRV" : "CF";
                                return (
                                  <span
                                    key={i}
                                    className={`inline-flex items-center gap-1 ${
                                      i === 0 ? "text-slate-700" : "text-slate-400"
                                    }`}
                                    title={`${when} · ${s.target}${s.provider_host ? ` · ${s.provider_host}` : ""}`}
                                  >
                                    <span className="inline-block px-1 rounded bg-slate-200 text-slate-700 text-[9px] font-semibold">
                                      {tag}
                                    </span>
                                    {dl} · {ul} · {lat}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{d.device_model || "—"}</TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {d.platform_version || "—"}
                      </TableCell>
                      <TableCell className="text-xs">{d.app_version || "—"}</TableCell>
                      <TableCell className="font-mono text-xs">{d.activation_key}</TableCell>
                      <TableCell>
                        {/* v1.4.66 — Owner badge (admin / reseller / client) */}
                        {d.owner_username ? (
                          <div className="flex flex-col items-start gap-0.5">
                            <span className="text-xs font-medium text-slate-800">
                              {d.owner_username}
                            </span>
                            <span
                              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
                                d.owner_role === "admin"
                                  ? "bg-purple-100 text-purple-700"
                                  : d.owner_role === "reseller"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-slate-100 text-slate-600"
                              }`}
                              data-testid={`device-owner-role-${d.id}`}
                            >
                              {d.owner_role || "client"}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        <div className="flex items-center gap-1.5">
                          {ipChurn && (
                            <span
                              className="w-1.5 h-1.5 rounded-full bg-amber-500"
                              title={`Seen from ${d.seen_ips.length} different IPs recently`}
                              data-testid={`ip-churn-dot-${d.id}`}
                            />
                          )}
                          <span className="truncate">{d.last_ip || "—"}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {/* v1.3.82 — per-device Pre-warm catalog badge.
                            Green when the device has at least one own bundle
                            on disk (`<pl>__<mac>.json.gz`), muted otherwise. */}
                        {bundleCount > 0 ? (
                          <Badge
                            variant="outline"
                            data-testid={`prewarm-count-${d.id}`}
                            className="border-emerald-200 bg-emerald-50 text-emerald-700 font-normal gap-1"
                            title={`${bundleCount} own pre-warm bundle(s) on disk`}
                          >
                            <Database className="w-3 h-3" />
                            {bundleCount}
                          </Badge>
                        ) : (
                          <span className="text-xs text-slate-400" data-testid={`prewarm-count-${d.id}`}>—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {/* v1.4.69 — Live "online / recent / stale /
                            offline" pill derived from last_seen age.
                            Distinct from the persistent `status` pill
                            below so admins can tell a stuck-active
                            device from a truly-online one at a glance. */}
                        {(() => {
                          const os = d.online_status || "unknown";
                          const map = {
                            online: "bg-emerald-500 text-white",
                            recent: "bg-amber-400 text-black",
                            stale: "bg-orange-500 text-white",
                            offline: "bg-slate-400 text-white",
                            unknown: "bg-slate-200 text-slate-600",
                          };
                          return (
                            <span
                              data-testid={`device-online-status-${d.id}`}
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${map[os] || map.unknown}`}
                              title={
                                d.last_seen_seconds != null
                                  ? `Last heartbeat ${d.last_seen_seconds}s ago`
                                  : "Never seen"
                              }
                            >
                              <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80"></span>
                              {os}
                            </span>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={`${statusColors[d.status] || statusColors.inactive} font-normal`}
                          variant="outline"
                        >
                          {d.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {d.last_seen ? new Date(d.last_seen).toLocaleString() : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          {/* v1.4.69 — Self-heal button. Resets a stuck
                              device row (blocked / inactive / stale
                              last_seen / bloated seen_ips) back to a
                              clean `active` state so the next heartbeat
                              writes fresh values. */}
                          <Button
                            data-testid={`btn-device-selfheal-${d.id}`}
                            size="sm"
                            variant="outline"
                            onClick={() => handleSelfHeal(d)}
                            className="text-sky-700"
                            title="Reset device (self-heal): forces status=active + fresh last_seen"
                          >
                            <Wand2 className="w-3.5 h-3.5 mr-1" />
                            Heal
                          </Button>
                          {d.status === "blocked" ? (
                            <Button
                              data-testid={ADMIN.btnDeviceUnblock(d.id)}
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleBlock(d)}
                              className="text-emerald-700"
                            >
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                              Unblock
                            </Button>
                          ) : (
                            <Button
                              data-testid={ADMIN.btnDeviceBlock(d.id)}
                              size="sm"
                              variant="outline"
                              onClick={() => handleToggleBlock(d)}
                              className="text-amber-700"
                            >
                              <Ban className="w-3.5 h-3.5 mr-1" />
                              Block
                            </Button>
                          )}
                          <Button
                            data-testid={ADMIN.btnDeleteDevice(d.id)}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(d.id)}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </>
            );
          })()}
        </CardContent>
      </Card>
    </div>
  );
}
